'use strict';

import cookies from '@/mixins/cookies.mixin';
import { createRouter, createWebHistory } from 'vue-router';
import store from '@/store';
import { getSelectedAccountId, isAccountsScopedRoute } from '@/utils/global-filters.util';
import { setPageTitle } from '@/utils/page-title.util';
import { consumeKeepQueryAccount, rememberReturnPath } from '@/utils/return-path.util';

import accounts from './accounts';
import admins from './admins';
import chargebacks from './chargebacks';
import dashboard from './dashboard';
import invitation from './invitation';
import home from './home';
import main from './main';
import payouts from './payouts';
import leads from './leads';
import sales from './sales';
import reports from './reports';
import transactions from './transactions';
import withdrawals from './withdrawals';
import activitys from './activitys';
import refunds from './refunds';
import risk from './risks';
import businessReviews from './business-reviews';
import webhookLogs from './webhook-logs';
import activations from './activations';
import twofactorauth from './twofactorauth';
import invoices from './invoices';
import rules from './rules';
import profiles from './profiles';
import whitelist from './whitelist';
import transfers from './transfers';
import BinManager from './bin-manager';  
import partners from './partners';
import commercialpartners from './commercialpartners';
import affiliations from './affiliations';
import affiliationCatalogs from './affiliation-catalogs';
import affiliationRequests from './affiliation-requests';
import acquirers from './acquirers';
import integrations from './integrations';
import timelines  from './timelines';
import tickets from './tickets';
import posInformation from './pos-information';
import posRentalFees from './pos-rental-fees';
import banks from './banks';
import terminalOrders from './terminal-orders';
import tapToPhoneDevices from './tap-to-phone-devices';
import directDebits from './direct-debits';
import globalAccounts from './global-accounts';
import aiConversations from './ai-conversations';
import aiKnowledge from './ai-knowledge';
import aiModels from './ai-models';
import aiOverview from './ai-overview';
import aiTools from './ai-tools';
import aiBehavior from './ai-behavior';
import aiGuardrails from './ai-guardrails';
import aiLearning from './ai-learning';
import subscriptions from './subscriptions';
import reconciliations from './reconciliations';
import prohibitedProducts from './prohibited-products';
import prohibitedProductReviews from './prohibited-product-reviews';
import arena from './arena';
import riskAnalysis from './risk-analysis';
import blacklistedCards from './blacklisted-cards';
import charts from './charts';
import notificationCampaigns from './notification-campaigns';
import auditLogs from './audit-logs';

const router = createRouter({
    history: createWebHistory(),
    routes: [
        ...accounts,
        ...admins,
        ...chargebacks,
        ...dashboard,
        ...home,
        ...invitation,
        ...main,
        ...payouts,
        ...leads,
        ...sales,
        ...transactions,
        ...reports,
        ...withdrawals,
        ...timelines,
        ...activitys,
        ...refunds,
        ...risk,
        ...businessReviews,
        ...webhookLogs,
        ...activations,
        ...twofactorauth,
        ...invoices,
        ...rules,
        ...whitelist,
        ...profiles,
        ...transfers,
        ...BinManager,
        ...partners,
        ...commercialpartners,
        ...affiliations,
        ...affiliationCatalogs,
        ...affiliationRequests,
        ...acquirers,
        ...integrations,
        ...tickets,
        ...posInformation,
        ...posRentalFees,
        ...banks,
        ...terminalOrders,
        ...tapToPhoneDevices,
        ...directDebits,
        ...subscriptions,
        ...globalAccounts,
        ...aiConversations,
        ...aiKnowledge,
        ...aiModels,
        ...aiOverview,
        ...aiTools,
        ...aiBehavior,
        ...aiGuardrails,
        ...aiLearning,
        ...reconciliations,
        ...prohibitedProducts,
        ...prohibitedProductReviews,
        ...arena,
        ...riskAnalysis,
        ...blacklistedCards,
        ...charts,
        ...notificationCampaigns,
        ...auditLogs,
    ]
});

router.beforeEach((to, from, next) => {
    const access_token = cookies.methods._getCookie('_tid');
    const twofactorauth = cookies.methods._getCookie('_tfa');
    const expired_password = cookies.methods._getCookie('_pexp');
    const public_routes = ['update-password', 'invitation-sign-up','recovery-password'];
    const auth_route = ['register-auth', 'two-step-auth', 'expired-password'];
    if (public_routes.includes(to.name)) {
        return next();
    }

    if(auth_route.includes(to.name) && access_token){
        return next();
    }

    if (to.name === 'login' && access_token && twofactorauth && !expired_password) {
        next({ name: 'home' });
    } else if (to.name !== 'login' && !access_token && !twofactorauth) {
        rememberReturnPath(to.fullPath);
        next({ name: 'login' });
    } else if ((to.name !== 'two-step-auth' && to.name !== 'register-auth') && access_token && !twofactorauth) {
        cookies.methods._deleteCookie('_tid');
        rememberReturnPath(to.fullPath);
        next({ name: 'login' });
    } else if (to.name !== 'expired-password' && access_token && twofactorauth && expired_password) {
        cookies.methods._deleteCookie('_tid');
        cookies.methods._deleteCookie('_tfa');
        cookies.methods._deleteCookie('_pexp');
        rememberReturnPath(to.fullPath);
        next({ name: 'login' });
    } else {
        redirectWithAccountFilter(to, next);
    }
});

function redirectWithAccountFilter(to, next) {
    if (consumeKeepQueryAccount()) {
        const filtered_account_id = getSelectedAccountId();
        const route_id = to.query?.id != null ? String(to.query.id) : null;
        if (filtered_account_id !== undefined && String(filtered_account_id) !== route_id) {
            store.dispatch('globalFilters/clearAccountFilter');
        }
        return next();
    }

    const filteredAccountId = getSelectedAccountId();
    if (filteredAccountId === undefined || !isAccountsScopedRoute(to.name)) {
        return next();
    }

    const targetId = String(filteredAccountId);
    const routeId = to.query?.id != null ? String(to.query.id) : null;

    if (to.name === 'accounts') {
        return next({
            name: 'accounts-info',
            query: { ...to.query, id: targetId },
        });
    }

    if (routeId !== targetId) {
        return next({
            name: to.name,
            query: { ...to.query, id: targetId },
            hash: to.hash,
        });
    }

    return next();
}

router.afterEach((to) => {
    setPageTitle(to.meta?.title);
});

export default router;